import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Layout
import AppLayout from './components/Layout/AppLayout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

// Auth Pages
import Login from './pages/Login';
// import Register from './pages/Register'; // Open registration disabled
import InvitationRegister from './pages/InvitationRegister';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Account from './pages/Account';

// App Pages
import Dashboard from './pages/Dashboard';
import Regions from './pages/Regions';
import Companies from './pages/Companies';
import People from './pages/People';
import Person from './pages/Person';
import Company from './pages/Company';
import Admin from './pages/Admin';
import AuditLogs from './pages/AuditLogs';
import DataQuality from './pages/DataQuality';
import WindUpTracker from './pages/WindUpTracker';
import Reports from './pages/Reports';
import PermissionsManagement from './pages/PermissionsManagement';
import UploaderMyCompanies from './pages/UploaderMyCompanies';
import UploaderCompanyUploadDesk from './pages/UploaderCompanyUploadDesk';
import { useAuth } from './hooks/useAuth';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
    },
    secondary: {
      main: '#FF9800',
    },
  },
});

function App() {
  const { hasAnyRole } = useAuth();
  const isUploaderOnly =
    hasAnyRole(['UPLOADER']) && !hasAnyRole(['ADMIN', 'SUPER_ADMIN', 'CORPORATE_ADMIN']);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        {/* Open registration disabled - invitation-only registration below */}
        <Route path="/register" element={<InvitationRegister />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Dashboard />} />
          <Route path="/my-companies" element={<UploaderMyCompanies />} />
          <Route path="/my-companies/:id" element={<UploaderCompanyUploadDesk />} />
          <Route
            path="/companies"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Companies />}
          />
          <Route
            path="/people"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <People />}
          />
          <Route
            path="/regions"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Regions />}
          />
          <Route
            path="/data-quality"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <DataQuality />}
          />
          <Route
            path="/wind-up"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <WindUpTracker />}
          />
          <Route
            path="/reports"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Reports />}
          />
          <Route
            path="/persons/:id"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Person />}
          />
          <Route
            path="/companies/:id"
            element={isUploaderOnly ? <Navigate to="/my-companies" replace /> : <Company />}
          />
          <Route path="/account" element={<Account />} />

          {/* Admin Routes - Require ADMIN or SUPER_ADMIN role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}>
                <PermissionsManagement />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer position="bottom-right" />
    </ThemeProvider>
  );
}

export default App;
